import { selectHasMatch } from 'util/selector/routerSelector';

import { AdminRoute } from 'app/route/admin';
import { ClientRoute } from 'app/route/client';
import { NavigationNode } from 'model/NavigationNode';
import { selectIsClientAdmin, selectUserRoles } from 'reduxStore/auth/selectors';
import {
  selectCurrentProjectId,
  selectCurrentPulseTemplate,
  selectInDevelopmentPulseProjects,
  selectIsEssentialPlan,
  selectPulseLifecycleProjects,
  selectIsMultiLanguageEnabled,
  selectHasHrisApiEnabled,
  selectLivePulseProjects,
} from 'reduxStore/project/projectSelector';
import { selectIsLifecycleSurveysEnabled } from 'reduxStore/project/projectSettings/projectSettingsSelector';
import { selectIsPulseTemplateFullyLoaded } from 'reduxStore/project/pulseProject/selectors';
import { selectCurrentProjectTemplateId } from 'reduxStore/projectTemplate/selectors';
import { AdminLeftMenuLinks } from 'register/AdminLeftMenuLinks';
import { ClientLeftMenuLinks } from 'register/ClientLeftMenuLinks';
import { ProjectTemplateAdminLeftMenuLinks } from 'register/ProjectTemplateAdminLeftMenuLinks';
import { createSelector } from 'reselect';

export const selectLeftMenuLinksForProject = createSelector(
  selectHasMatch(AdminRoute.PROJECT_PATTERN),
  selectCurrentProjectId,
  (hasMatch, projectId) => {
    if (!hasMatch) return false;

    return AdminLeftMenuLinks.project(projectId);
  },
);

export const selectLeftMenuLinksForPulseProject = createSelector(
  selectHasMatch(ClientRoute.PROJECT_PATTERN),
  selectCurrentPulseTemplate,
  selectIsClientAdmin,
  selectIsEssentialPlan,
  selectInDevelopmentPulseProjects,
  selectLivePulseProjects,
  selectPulseLifecycleProjects,
  selectIsMultiLanguageEnabled,
  selectHasHrisApiEnabled,
  selectIsLifecycleSurveysEnabled,
  selectIsPulseTemplateFullyLoaded,
  (
    hasMatch,
    project,
    isClientAdmin,
    isEssentialPlan,
    inDevelopmentPulseProjects,
    livePulseProjects,
    pulseLifecycleProjects,
    isMultiLanguageEnabled,
    isHrisApiEnabled,
    isLifecycleSurveysEnabled,
    isPulseTemplateFullyLoaded,
  ) =>
    hasMatch &&
    project &&
    ClientLeftMenuLinks.project({
      project,
      inDevelopmentPulseProjects,
      livePulseProjects,
      isClientAdmin,
      isEssentialPlan,
      pulseLifecycleProjects,
      isMultiLanguageEnabled,
      isHrisApiEnabled,
      isLifecycleSurveysEnabled: !!isLifecycleSurveysEnabled,
      isPulseTemplateFullyLoaded,
    }),
);

export const selectLeftMenuLinksForProjectTemplate = createSelector(
  selectHasMatch(AdminRoute.PROJECT_TEMPLATE_PATTERN),
  selectCurrentProjectTemplateId,
  (hasMatch, projectTemplateId) =>
    hasMatch && ProjectTemplateAdminLeftMenuLinks.projectTemplate(projectTemplateId),
);

export const selectAllLeftMenuLinks = createSelector(
  selectLeftMenuLinksForProject,
  selectLeftMenuLinksForProjectTemplate,
  selectLeftMenuLinksForPulseProject,
  (...links: NavigationNode[][]) => links.find((possibleLinks) => !!possibleLinks) || [],
);

const hasAccess =
  (roles: string[]) =>
  ({ requiredRoles = [] }: NavigationNode): boolean => {
    return requiredRoles.every((requiredRole) => !!roles.find((role) => requiredRole === role));
  };

const filterChildrenByRequiredRoles =
  (roles: string[]) =>
  (node: NavigationNode): NavigationNode => {
    return {
      ...node,
      children: node.children ? node.children.filter(hasAccess(roles)) : undefined,
    };
  };

export const selectLeftMenuLinks = createSelector(
  selectAllLeftMenuLinks,
  selectUserRoles,
  (links, roles) => {
    return links.map(filterChildrenByRequiredRoles(roles));
  },
);
